import { ReactNode, useState } from 'react';
import { lt, useFeatureFlag, useFormControls } from '@mews/framework';
import { WithScope } from '@mews-commander/types';
import { FeatureFlag, SearchType } from '@mews-commander/enums';
import { useCommanderSelector, useGainsightCommanderEvents } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getReservationsCustomerIds } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { FormLayout } from '../../FormLayout';
import { SearchHookField } from '../../../../../components/form/hookFields';
import { getCheckedReservationCompanionIds } from '../selectors';
import { useRoutingRuleChangesConfirmation } from '../../../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';
import { GroupExistingCustomerFormKeys, GroupExistingCustomerFormValues, defaultValues, resolver } from './types';
import { useAddCompanionshipsMutation } from './useAddCompanionshipsMutation';

interface GroupExistingCustomerProps extends WithScope {
    reservationId: string;
    onSuccess?: () => void;
    canCreateNewOne?: boolean;
}

export const GroupExistingCustomer = ({
    scope,
    onSuccess,
    reservationId,
    canCreateNewOne = false,
}: GroupExistingCustomerProps) => {
    const checkedReservationCompanionIds = useCommanderSelector((state) =>
        getCheckedReservationCompanionIds(state, { scope, groupId: scope })
    );

    const enterpriseId = useCurrentEnterpriseId();
    const currentAccountIds = useCommanderSelector((state) =>
        getReservationsCustomerIds(state, { ids: [reservationId], enterpriseId })
    );

    const { mutate, isPending } = useAddCompanionshipsMutation({
        groupId: scope,
        reservationId,
    });
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);

    const handleSubmit = (values: GroupExistingCustomerFormValues) => {
        const result = validate();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        mutate(values, {
            onSuccess: () => {
                onSuccess?.();
                reset();
            },
        });
    };

    const { renderFormContent, reset, watch } = useFormControls({
        onSubmit: handleSubmit,
        resolver,
        defaultValues,
        dataTestForm: 'group-existing-customer-form',
        id: `companionship-add-form-${scope}`,
    });

    const gainsightEvents = useGainsightCommanderEvents();
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const customerId = watch(GroupExistingCustomerFormKeys.companionId);

    const { validate, isLoading } = useRoutingRuleChangesConfirmation({
        reservationIds: [reservationId],
        accountIds: [...currentAccountIds, customerId],
        enabled: granularRoutingRulesConditionsExtensionEnabled && !!customerId,
        scope,
        onSubmit: ({ changes }) => {
            const onInternalSuccess = () => {
                gainsightEvents.onApiSuccess({
                    context: 'BillingAutomationChanged',
                    data: {
                        changes,
                        origin: 'Account',
                    },
                });
                onSuccess?.();
                reset();
            };

            mutate(watch(), { onSuccess: onInternalSuccess });
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => {
            reset();
            setRoutingRuleChangesDialog(null);
        },
    });

    return (
        <>
            {renderFormContent(
                <FormLayout
                    vertical
                    loading={isPending}
                    submitButtonLoading={isLoading}
                    submitButtonText={lt('AddExistingCustomerSubmitButton')}
                    title={lt('AddExistingCustomer')}
                >
                    <SearchHookField
                        data-test-id={GroupExistingCustomerFormKeys.companionId}
                        newEntity={canCreateNewOne}
                        name={GroupExistingCustomerFormKeys.companionId}
                        placeholder={lt('SearchByName')}
                        label={lt('Customer')}
                        searchTypes={[SearchType.Customer]}
                        except={checkedReservationCompanionIds} // Exclude the current group from the search
                    />
                </FormLayout>
            )}
            {routingRuleChangesDialog}
        </>
    );
};
